import { Message } from 'zk-ui';
import { useInstance } from 'common-module/src/util/useInstance';
import http from 'common-module/src/util/http';

import { FieldSelectOption } from '../models/field';
import {
  IssueArea,
  IssueAssembleResponse,
  IssueSubTask,
  IssueType,
  ProjectRole,
  Sprint,
  realPaths
} from '../models/issue';
import { Member } from '../models/member';
import { ChangeRecord } from '../models/record';
import { WorkflowInfo } from '../models/workflow';

interface IssueAssembleFunctions {
  loadIssueData?: boolean;
  loadIssueRelationship?: boolean;
  loadIssueSubTask?: boolean;
  loadIssueType?: boolean;
  loadLayoutData?: boolean;
  loadSprint?: boolean;
}

export async function getIssueAssembleInfo(
  issueOrProjectAndType:
    | { issueId: number }
    | { projectId: number; issueTypeId?: number },
  assembleFunctions: IssueAssembleFunctions,
  cachedLastModifiedTime?: number,
  cachedLayoutLastModifiedTime?: number,
  options: Record<string, any> = {}
): Promise<IssueAssembleResponse> {
  return http.post('/issue/issue-assemble-info', {
    ...issueOrProjectAndType,
    ...assembleFunctions,
    cachedLastModifiedTime,
    cachedLayoutLastModifiedTime
  }, {
    assertResult: (res: any) => {
      const instance = useInstance();
      if (instance && res.data.code === '008') {
        instance?.showNoPermissionAlert();
        instance.eventBus.$emit('issue:closed');
        return false;
      }
      return true;
    },
    showError: false,
    ...options
  });
}

export async function getIssueTypes(projectId: number): Promise<IssueType[]> {
  return http.post('/issue-type/list-selector/project-id', {
    projectId
  });
}

export async function getSprints(projectId: number): Promise<Sprint[]> {
  return http.post('/sprint/list-by-project-id-useful', {
    id: projectId
  });
}

export async function getProjectRoles(
  projectId: number
): Promise<ProjectRole[]> {
  return http.post(
    '/member/project-user-info',
    {
      projectId
    },
    {
      showError: false
    }
  );
}

export async function getSelectOptions(
  param: { projectId: number; issueTypeId: number } | { issueId: number }
): Promise<FieldSelectOption[]> {
  return http.post('/issue/more-select-options', param, { showError: false });
}

export async function updateIssue(
  id: number,
  diff: Record<string, any>
): Promise<ChangeRecord[]> {
  // return http.post('/issue/update-v2', { id, data: diff });
  return http.post('/issue/update', { id, data: diff });
}

export async function archivedIssue(params: {
  issueId: number;
  isArchived: boolean;
}) {
  return http.post('/issue/archived-issue', params);
}

export async function createIssue(values: Record<string, any>): Promise<{
  subtask: IssueSubTask[];
  id: number;
}> {
  try {
    const res = await http.post('/issue/add-v2', values, { showError: false });
    return res;
  } catch (err: any) {
    if (err.message) {
      Message.error({
        message: err.message,
        dangerouslyUseHTMLString: true
      });
    }
    throw err;
  }
}

export async function getIssueLayout(
  projectId: number,
  issueTypeId: number
): Promise<IssueArea[]> {
  return http.post('/issue/load-layout', { projectId, issueTypeId });
}

export async function getMemberList(projectId: number): Promise<Member[]> {
  return http.post(
    '/member/project-users',
    {
      projectId,
      containAccUnEnabled: true
    },
    {
      showError: false
    }
  );
}

/**
 * 创建评论
 * @param issueId 事项ID
 * @param content 内容
 * @param commentAttributes 额外内容
 * @returns {Promise<*>}
 */
export const createComment = async (
  issueId: number,
  html: string,
  text: string,
  commentAttributes: any[],
  files: Array<{ fileKey: string; sequence: number }>
) => {
  return http.post('/sys-comment/issue/add-comment', {
    content: html,
    plainContent: text,
    linkId: issueId,
    commentAttributes,
    files
  });
};

export const getStickyTop = async (
  projectId: number,
  issueTypeId: number
): Promise<
  Array<{
    type: 'top' | 'tab_top';
    fieldCode: string;
  }>
> => {
  return http.post('/issue/user-custom-field-layout/get', {
    issueTypeId,
    projectId
  });
};
export const setStickyTop = async (
  projectId: number,
  issueTypeId: number,
  fields: Array<{
    issueTypeId: number;
    projectId: number;
    customFieldId: number;
    fieldCode: string;
    ordinate: number;
    areaName: 'right area' | 'left area';
    type: 'top' | 'tab_top';
  }>
) => {
  return http.post(
    '/issue/user-custom-field-layout/set',
    {
      projectId,
      issueTypeId,
      userCustomFieldLayoutList: fields
    },
    {
      showError: false
    }
  );
};

export async function getWorkflow(workflowId: number): Promise<WorkflowInfo> {
  return http.post(
    '/workflow/get/layout',
    {
      id: workflowId
    },
    {
      showError: false
    }
  );
}

export async function getRecentlyList(data: Record<string, any>) {
  return http.post('/issue/recently-issue-list', data);
}

/* 用户最近行为上报 */
// #if private
// #code export const userReportBehavior = () => {};
// #else
export async function userReportBehavior(data: Record<string, any>) {
  return http.post(`/relationship/${data.issueId}/behavior`, data, { showError: false });
}
// #endif
export async function getLinkPreviewInfo(url: string) {
  return http.get('page-preview/getPage', { url });
}

/**
 * 获取文件的真实地址（现在是一个重定向地址）
 * @param fileKeys 文件的key[]
 * @returns {Promise<*>}
 */
export const getFileRealUrls = async (fileKeys: string[]): Promise<realPaths[]> => {
  return http.post('/sys-file/download/get-download-paths', { fileKeys });
};

/**
 * 获取issue文档列表
 * @param issueId 事项ID
 * @returns {Promise<*>}
 */
export const getIssueDoc = async (issueId: number): Promise<{citedList: any[], beMentionedList: any[]}> => {
  return http.post('/doc-related-issue/list-doc', { issueId });
};
